import React, { useState } from "react";
import axios from "axios";
import { Mail, Check, Sparkles, Send, Gift, Rocket, Loader2 } from "lucide-react";
import Reveal from "./Reveal";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useToast } from "../hooks/use-toast";
import { getStoredRef } from "../lib/referral";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const submit = async (e) => {
    e.preventDefault();
    if (!email.includes("@")) {
      toast({ title: "Hold on", description: "Please enter a valid email address." });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email: email.trim(), source: "newsletter-section", ref: getStoredRef() }, { timeout: 15000 });
      setDone(true);
      trackEvent("sign_up", { method: "newsletter-section" });
      toast({ title: "You're in! 🎉", description: "Early access + your free Finn wallpaper pack are ready below." });
      setEmail("");
    } catch (err) {
      toast({ title: "Oops", description: "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="newsletter" className="py-20 sm:py-24">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2.5rem] ocean-gradient px-6 py-12 sm:px-16 sm:py-20 text-center shadow-[0_30px_80px_-20px_rgba(14,127,168,0.55)] border-4 border-white/30">
            {/* decorative accents */}
            <div className="absolute -top-12 -right-12 w-56 h-56 bg-white/20 rounded-full blur-2xl animate-blob" />
            <div className="absolute -bottom-16 -left-10 w-64 h-64 bg-cyan-200/20 rounded-full blur-2xl animate-blob" />
            {[...Array(5)].map((_, i) => (
              <span
                key={i}
                className="absolute bottom-10 rounded-full bg-white/40 animate-bubble"
                style={{ left: `${12 + i * 18}%`, width: 10 + i * 4, height: 10 + i * 4, animationDelay: `${i * 0.7}s` }}
              />
            ))}

            <div className="relative z-10 max-w-2xl mx-auto">
              <span className="inline-flex items-center gap-2 rounded-full bg-white text-teal-700 px-4 py-1.5 text-sm font-bold mb-5 sticker -rotate-2">
                <Sparkles className="w-4 h-4" /> Join the crew
              </span>
              <h3 className="font-display font-800 text-4xl sm:text-6xl text-white leading-[0.95]">
                Stay in the loop.
              </h3>
              <p className="text-white/95 text-lg sm:text-xl mt-4 font-medium">
                Get everything Sharkbowl Games: new releases, dev diaries and behind-the-scenes goodies, straight to your inbox.
              </p>

              {/* Incentive perks */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white text-sm font-semibold px-4 py-2 backdrop-blur-sm">
                  <Rocket className="w-4 h-4" /> Early access to our games
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-white/15 text-white text-sm font-semibold px-4 py-2 backdrop-blur-sm">
                  <Gift className="w-4 h-4" /> Free Finn wallpaper pack
                </span>
              </div>

              <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                <div className="relative flex-1">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-teal-600" />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@email.com"
                    data-testid="newsletter-email-input"
                    className="h-14 pl-12 rounded-full bg-white border-0 text-slate-800 text-base shadow-lg"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="newsletter-submit-button"
                  className="h-14 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-8 font-bold shrink-0 shadow-lg hover:-translate-y-0.5 transition-transform disabled:opacity-70"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : done ? <><Check className="w-5 h-5 mr-1.5" /> Subscribed</> : <>Subscribe <Send className="w-4 h-4 ml-1.5" /></>}
                </Button>
              </form>
              <p className="text-white/70 text-sm mt-4">
                No spam, just games. Unsubscribe anytime.
              </p>

              {done && (
                <div className="mt-6 max-w-md mx-auto bg-white/10 border border-white/20 rounded-2xl px-5 py-4 backdrop-blur-sm">
                  <p className="flex items-center justify-center gap-2 font-display font-700 text-white">
                    <Check className="w-5 h-5" /> You're in!
                  </p>
                  <p className="text-white/85 text-sm mt-1">
                    Check your inbox to confirm your email — your free Finn wallpaper pack is on its way. 🦈
                  </p>
                </div>
              )}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
